import React, { useMemo, useState } from 'react';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import WhatsAppTemplatePreview, { previewFromLibraryItem } from './WhatsAppTemplatePreview';

function buildLibraryButtonInputs(libraryButtons, userInputs) {
  if (!libraryButtons?.length) return undefined;
  const mapped = libraryButtons
    .map((btn, idx) => {
      const input = userInputs[idx] || {};
      const type = (btn.type || '').toUpperCase();
      if (type === 'URL') {
        const baseUrl = input.base_url?.trim() || btn.url || 'https://www.example.com/{{1}}';
        return {
          type: 'URL',
          url: {
            base_url: baseUrl,
            url_suffix_example: input.url_suffix_example?.trim() || baseUrl.replace(/\{\{1\}\}/, 'demo')
          }
        };
      }
      if (type === 'PHONE_NUMBER') {
        const phone = input.phone_number?.trim() || btn.phone_number || '';
        if (!phone) return null;
        return { type: 'PHONE_NUMBER', phone_number: phone };
      }
      return null;
    })
    .filter(Boolean);
  return mapped.length ? mapped : undefined;
}

const LibraryTemplateSidebarForm = ({
  libraryItem,
  workspaceId,
  onCustomize,
  onSuccess,
  onCancel
}) => {
  const [templateName, setTemplateName] = useState('');
  const [buttonInputs, setButtonInputs] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const previewForm = useMemo(
    () => ({ ...previewFromLibraryItem(libraryItem), name: templateName }),
    [libraryItem, templateName]
  );

  const needsButtonInputs = useMemo(() => {
    return (libraryItem?.buttons || []).some((b) =>
      ['URL', 'PHONE_NUMBER'].includes((b.type || '').toUpperCase())
    );
  }, [libraryItem]);

  const handleSubmit = async () => {
    if (!templateName.trim()) {
      toast.error('Enter a name for your template');
      return;
    }

    const libraryButtons = libraryItem?.buttons || [];
    const userInputs = libraryButtons.map((_, idx) => buttonInputs[idx] || {});
    const library_template_button_inputs = buildLibraryButtonInputs(libraryButtons, userInputs);

    if (needsButtonInputs) {
      const missingPhone = libraryButtons.some(
        (b, idx) =>
          (b.type || '').toUpperCase() === 'PHONE_NUMBER' &&
          !(buttonInputs[idx]?.phone_number?.trim() || b.phone_number)
      );
      const missingUrl = libraryButtons.some(
        (b, idx) =>
          (b.type || '').toUpperCase() === 'URL' &&
          !(buttonInputs[idx]?.base_url?.trim() || b.url)
      );
      if (missingPhone || missingUrl) {
        toast.error('Fill in required button details');
        return;
      }
    }

    setSubmitting(true);
    try {
      const headers = { 'x-workspace-id': workspaceId };
      const res = await apiClient.post(
        '/api/whatsapp/templates/manage/from-library',
        {
          name: templateName.trim().toLowerCase(),
          language: libraryItem.language || 'en_US',
          category: libraryItem.category || 'UTILITY',
          library_template_name: libraryItem.name,
          library_template_button_inputs
        },
        { headers }
      );
      const tpl = res.data.template || {};
      onSuccess?.({
        name: templateName.trim().toLowerCase(),
        language: libraryItem.language || 'en_US',
        status: tpl.status || res.data.template?.status || 'PENDING',
        message: res.data.message
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create template');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
      <div className="flex-1 min-w-0 overflow-y-auto p-5 space-y-4">
        <div className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3">
          From Meta library: <strong>{libraryItem.name}</strong>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Your template name *</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) =>
              setTemplateName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))
            }
            placeholder="e.g. my_delivery_update"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>

        {needsButtonInputs && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Button settings</p>
            {(libraryItem.buttons || []).map((btn, idx) => {
              const type = (btn.type || '').toUpperCase();
              if (type !== 'URL' && type !== 'PHONE_NUMBER') return null;
              return (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 text-sm">
                  <p className="font-medium mb-1">{btn.text || type}</p>
                  {type === 'URL' && (
                    <input
                      type="url"
                      value={buttonInputs[idx]?.base_url ?? btn.url ?? ''}
                      onChange={(e) =>
                        setButtonInputs((prev) => ({
                          ...prev,
                          [idx]: { ...prev[idx], base_url: e.target.value }
                        }))
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1"
                      placeholder="https://yoursite.com/{{1}}"
                    />
                  )}
                  {type === 'PHONE_NUMBER' && (
                    <input
                      type="text"
                      value={buttonInputs[idx]?.phone_number ?? btn.phone_number ?? ''}
                      onChange={(e) =>
                        setButtonInputs((prev) => ({
                          ...prev,
                          [idx]: { ...prev[idx], phone_number: e.target.value }
                        }))
                      }
                      className="w-full border border-gray-300 rounded px-2 py-1 text-sm mt-1"
                      placeholder="+919876543210"
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
          Submitted as UTILITY via Meta. Status will show as PENDING until Meta approves.
        </p>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onCustomize?.(libraryItem)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Customize
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Add to my templates'}
          </button>
        </div>
      </div>

      <div className="w-72 lg:w-80 shrink-0 border-l border-gray-200 bg-[#f0f2f5] p-3 overflow-y-auto overflow-x-hidden hidden md:flex md:flex-col md:items-center">
        <WhatsAppTemplatePreview form={previewForm} sidebar />
      </div>
      <div className="md:hidden border-t border-gray-200 bg-[#f0f2f5] p-4 max-h-72 overflow-y-auto shrink-0">
        <WhatsAppTemplatePreview form={previewForm} compact />
      </div>
    </div>
  );
};

export default LibraryTemplateSidebarForm;
